import { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Home, MapPin, Bed, Bath, Maximize, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API, AppContext } from '@/App';

const SimilarProperties = ({ property, limit = 3 }) => {
  const { t } = useTranslation();
  const { formatPrice, formatArea } = useContext(AppContext);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSimilar = useCallback(async () => {
    if (!property?.id) return;
    
    try {
      const params = new URLSearchParams({
        exclude_id: property.id,
        property_type: property.property_type || '',
        transaction_type: property.transaction_type || '',
        department: property.department || '',
        limit: String(limit)
      });
      
      const res = await axios.get(`${API}/properties/similar?${params}`);
      setSimilar(res.data || []);
    } catch (error) {
      console.error('Error fetching similar properties:', error);
      setSimilar([]);
    } finally {
      setLoading(false);
    }
  }, [property?.id, property?.property_type, property?.transaction_type, property?.department, limit]);

  useEffect(() => {
    fetchSimilar();
  }, [fetchSimilar]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  if (similar.length === 0) {
    return null;
  }

  return (
    <div className="mt-12" data-testid="similar-properties-section">
      <h2 className="text-2xl text-white mb-6 flex items-center gap-3">
        <Home className="text-[#D4AF37]" size={28} />
        {t('property.similarProperties', 'Propiedades Similares')}
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {similar.map((prop, index) => (
          <motion.div
            key={prop.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link 
              to={`/propiedad/${prop.slug || prop.id}`}
              className="block bg-[#121212] border border-white/5 overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
              data-testid={`similar-property-${index}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={prop.images?.[0]?.url || 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg'}
                  alt={prop.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3">
                  <span className="bg-[#D4AF37] text-black text-xs px-2 py-1 font-medium">
                    {prop.transaction_type}
                  </span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4">
                <h3 className="text-white font-medium line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                  {prop.name}
                </h3>
                
                <p className="text-[#A3A3A3] text-sm mt-2 flex items-center gap-1">
                  <MapPin size={14} className="text-[#D4AF37]" />
                  {prop.zone}, {prop.department}
                </p>
                
                <p className="text-[#D4AF37] font-mono text-lg mt-3">
                  {formatPrice(prop.price_usd)}
                </p>
                
                {/* Features */}
                <div className="flex items-center gap-4 mt-3 text-[#A3A3A3] text-sm">
                  {prop.bedrooms > 0 && (
                    <span className="flex items-center gap-1">
                      <Bed size={14} /> {prop.bedrooms}
                    </span>
                  )}
                  {prop.bathrooms > 0 && (
                    <span className="flex items-center gap-1">
                      <Bath size={14} /> {prop.bathrooms}
                    </span>
                  )}
                  {prop.area_m2 > 0 && (
                    <span className="flex items-center gap-1">
                      <Maximize size={14} /> {formatArea(prop.area_m2)}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default SimilarProperties;
